import { Image } from '../../../modules/design-system';

export interface ShippingItemCardProps {
  imageUrl: string | null;
  title: string;
}

// Same two-layer glass background as the outer DeliveryFormCard wrapper —
// kept literal so the design-constructor invariant scanner can see every
// `--co-DeliveryFormCard-*` token it consumes.
const SHIPPING_ITEM_BG =
  'linear-gradient(' +
  'var(--co-DeliveryFormCard-bgDirection, to bottom), ' +
  'color-mix(in srgb, var(--co-DeliveryFormCard-bgFrom, rgba(255,255,255,0.20)) ' +
  'calc(var(--co-DeliveryFormCard-bgOpacity, 1) * 100%), transparent) 0%, ' +
  'color-mix(in srgb, var(--co-DeliveryFormCard-bgTo, rgba(153,153,153,0.20)) ' +
  'calc(var(--co-DeliveryFormCard-bgOpacity, 1) * 100%), transparent) 100%), ' +
  'linear-gradient(90deg, ' +
  'var(--co-DeliveryFormCard-darkTint, rgba(0,0,0,0.18)) 0%, ' +
  'var(--co-DeliveryFormCard-darkTint, rgba(0,0,0,0.18)) 100%)';

/**
 * Single tile in the shipping list grid — same shape on both Delivery
 * pages. Matches Figma 710:4374: one rounded grey slab, image takes
 * almost the full card width with a 2px gutter and its own 16px
 * radius, bold white title sits centred below.
 */
export function ShippingItemCard({ imageUrl, title }: ShippingItemCardProps) {
  return (
    <div
      className="flex w-full flex-col rounded-[24px] p-0.5"
      style={{
        backgroundImage: SHIPPING_ITEM_BG,
        backdropFilter: 'blur(var(--co-MyPacksItemCard-glassBlur, 27px))',
        WebkitBackdropFilter: 'blur(var(--co-MyPacksItemCard-glassBlur, 27px))',
        borderStyle: 'solid',
        borderWidth: 'var(--co-MyPacksItemCard-borderWidth, 1px)',
        borderColor: 'var(--co-MyPacksItemCard-borderColor, rgba(171,174,179,0.20))',
      }}
    >
      <div className="relative aspect-square w-full overflow-hidden rounded-2xl">
        <Image
          src={imageUrl}
          alt={title}
          className="absolute inset-0 h-full w-full"
          imgClassName="absolute inset-0 h-full w-full object-contain"
          quality={90}
        />
      </div>
      <div className="px-3 pt-3 pb-4 text-center">
        <p
          className="text-subhead font-bold leading-caption-1 tracking-[-0.2px]"
          style={{
            color: '#FFFFFF',
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
            overflow: 'hidden',
            wordBreak: 'break-word',
            // Belt-and-braces: cap the physical height to exactly two
            // line-heights so even browsers that mis-handle line-clamp
            // can't paint a third row.
            maxHeight: 'calc(var(--leading-caption-1) * 2)',
            margin: 0,
          }}
        >
          {title}
        </p>
      </div>
    </div>
  );
}
